import { MeetingType } from 'api/generated/types/meetingType';
import type { DomainSingularKey } from 'utils/domainText';
import { domainTextOr } from 'utils/domainTextFallback';
import { enumValues, hasOwnKey, isEnumValue } from 'utils/typeGuards';

/** English labels, keyed by type; meetingTypeLabel translates. */
export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  interview: 'Candidate Interview',
  intake: 'Hiring Manager Intake',
  sales: 'Staffing Sales',
  staffing_discovery: 'Staffing Sales Discovery',
  other: 'Other',
};

export const MEETING_TYPE_LABEL_KEYS = {
  interview: 'meetingType.interview',
  intake: 'meetingType.intake',
  sales: 'meetingType.sales',
  staffing_discovery: 'meetingType.staffingDiscovery',
  other: 'meetingType.other',
} as const satisfies Record<MeetingType, DomainSingularKey>;

const isMeetingType = (type: string): type is MeetingType =>
  hasOwnKey(MEETING_TYPE_LABELS, type);

/** Translated meeting type label, resolved at call time. */
export const meetingTypeLabel = (type?: string | null): string => {
  if (!type) return '-';
  return isMeetingType(type)
    ? domainTextOr(MEETING_TYPE_LABEL_KEYS[type], MEETING_TYPE_LABELS[type])
    : type;
};

/** Meeting type options with labels translated at call time. */
export const getMeetingTypeOptions = (): {
  value: MeetingType;
  label: string;
}[] =>
  enumValues(MeetingType).map((value) => ({
    value,
    label: meetingTypeLabel(value),
  }));

/** English options; prefer getMeetingTypeOptions() for display. */
export const MEETING_TYPE_OPTIONS: { value: MeetingType; label: string }[] =
  enumValues(MeetingType).map((value) => ({
    value,
    label: MEETING_TYPE_LABELS[value],
  }));

// Intake (hiring-manager requirements) and staffing-discovery (sales) meetings
// have no candidate, so the candidate picker is hidden and the candidate column
// is blank for them. Mirrors MeetingType.has_candidate on the backend.
const MEETING_TYPES_WITHOUT_CANDIDATE: ReadonlySet<MeetingType> = new Set([
  'intake',
  'staffing_discovery',
]);

export const meetingTypeHasCandidate = (type?: string | null): boolean =>
  !(
    isEnumValue(MeetingType, type) && MEETING_TYPES_WITHOUT_CANDIDATE.has(type)
  );
